import React, { useCallback, useEffect, useState } from 'react';

import PageHeader from '../../components/PageHeader.jsx';
import Loader, { ErrorBox } from '../../components/Loader.jsx';
import { SuperAdminAPI } from '../../lib/api';
import { notify } from '../../lib/notifications';


function memberLabel(member) {
  const who = member.fullname || member.mobile_number || member.email || `Member ${member.id}`;
  const contact = [member.mobile_number, member.email].filter(Boolean).join(' · ');
  const company = member.company_name ? ` · ${member.company_name}` : '';
  return contact ? `${who} (${contact})${company}` : `${who}${company}`;
}

/**
 * Super Admin → SuccessYatra move.
 *
 * Moves one member's SuccessYatra rows between the public site and an
 * organisation, or from one organisation to another. Nothing outside
 * SuccessYatra is copied.
 */
export default function SuccessYatraMove() {
  const [workspaces, setWorkspaces] = useState([]);
  const [source, setSource] = useState('public');
  const [destination, setDestination] = useState('public');
  const [query, setQuery] = useState('');
  const [members, setMembers] = useState([]);
  const [memberId, setMemberId] = useState('');
  const [companies, setCompanies] = useState([]);
  const [companyId, setCompanyId] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    SuperAdminAPI.successYatraMove.workspaces()
      .then((payload) => {
        if (cancelled) return;
        const rows = payload.workspaces || [];
        setWorkspaces(rows);
        if (rows[0]) setSource(rows[0].key);
        if (rows[0]) setDestination(rows[0].key);
      })
      .catch((err) => { if (!cancelled) setError(err); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const loadMembers = useCallback(async (workspace, text) => {
    setError(null);
    try {
      const payload = await SuperAdminAPI.successYatraMove.members(workspace, text);
      setMembers(payload.members || []);
      setMemberId('');
    } catch (err) {
      setError(err);
      setMembers([]);
    }
  }, []);

  useEffect(() => {
    if (!source) return undefined;
    const handle = setTimeout(() => { loadMembers(source, query); }, 250);
    return () => clearTimeout(handle);
  }, [source, query, loadMembers]);

  useEffect(() => {
    if (!destination) return undefined;
    let cancelled = false;
    SuperAdminAPI.successYatraMove.companies(destination)
      .then((payload) => {
        if (cancelled) return;
        setCompanies(payload.companies || []);
        setCompanyId('');
      })
      .catch((err) => { if (!cancelled) setError(err); });
    return () => { cancelled = true; };
  }, [destination]);

  const submit = async (event) => {
    event.preventDefault();
    if (!memberId) {
      notify.error('Choose a SuccessYatra member.');
      return;
    }
    const person = members.find((row) => String(row.id) === String(memberId));
    const from = workspaces.find((row) => row.key === source);
    const to = workspaces.find((row) => row.key === destination);
    const company = companies.find((row) => String(row.id) === String(companyId));
    const where = company ? `${to?.label || destination} / ${company.name}` : (to?.label || destination);
    const ok = window.confirm(
      `Move ${person ? memberLabel(person) : 'this member'} from ${from?.label || source} to ${where}? Only their SuccessYatra records move.`,
    );
    if (!ok) return;
    setSaving(true);
    setError(null);
    try {
      const result = await SuperAdminAPI.successYatraMove.move({
        source,
        destination,
        member_id: Number(memberId),
        destination_company_id: companyId ? Number(companyId) : null,
      });
      notify.success(result.mode === 'reassign'
        ? 'SuccessYatra company updated.'
        : 'SuccessYatra member moved.');
      setMemberId('');
      await loadMembers(source, query);
    } catch (err) {
      setError(err);
    } finally {
      setSaving(false);
    }
  };

  if (loading && workspaces.length === 0) return <Loader label="Loading workspaces…" />;

  return (
    <>
      <PageHeader
        title="SuccessYatra move"
        subtitle="Move one member from a company to another company, from a company to the public site, or from the public site into a company. Dreams, goals, training, customers, and the rest of their SuccessYatra record go with them. Tasks, complaints, and other Taskydo data stay put."
      />
      <ErrorBox error={error} />
      <form onSubmit={submit} style={{ maxWidth: 640 }}>
        <div className="ty-field">
          <label htmlFor="sy-source">From</label>
          <select id="sy-source" className="ty-select" value={source} onChange={(event) => setSource(event.target.value)}>
            {workspaces.map((row) => (
              <option key={row.key} value={row.key}>{row.label}</option>
            ))}
          </select>
        </div>
        <div className="ty-field">
          <label htmlFor="sy-search">Find the member</label>
          <input
            id="sy-search"
            className="ty-input"
            value={query}
            placeholder="Name, mobile, or email"
            onChange={(event) => setQuery(event.target.value)}
          />
        </div>
        <div className="ty-field">
          <label htmlFor="sy-member">Member</label>
          <select id="sy-member" className="ty-select" value={memberId} onChange={(event) => setMemberId(event.target.value)}>
            <option value="">Select a member</option>
            {members.map((row) => (
              <option key={row.id} value={row.id}>{memberLabel(row)}</option>
            ))}
          </select>
        </div>
        <div className="ty-field">
          <label htmlFor="sy-dest">To</label>
          <select id="sy-dest" className="ty-select" value={destination} onChange={(event) => setDestination(event.target.value)}>
            {workspaces.map((row) => (
              <option key={`to-${row.key}`} value={row.key}>{row.label}</option>
            ))}
          </select>
        </div>
        <div className="ty-field">
          <label htmlFor="sy-company">SuccessYatra company in the destination</label>
          <select id="sy-company" className="ty-select" value={companyId} onChange={(event) => setCompanyId(event.target.value)}>
            <option value="">No company (public member)</option>
            {companies.map((row) => (
              <option key={row.id} value={row.id}>{row.name}</option>
            ))}
          </select>
        </div>
        <button type="submit" className="ty-btn" disabled={saving}>
          {saving ? 'Moving…' : 'Move SuccessYatra member'}
        </button>
      </form>
    </>
  );
}
